import React from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import EditorDialog from '../common/EditorDialog';
import { getSupabaseClient } from '../../data/supabase';
import useSessionStore from '../../state/session';
import { safeErrorMessage } from '../../utils/appErrors';
import './PhotoViewer.css';

export type ViewerPhoto = { id: string; title: string; url?: string | null; storagePath?: string; bucket?: string };

/** Original image in a native modal: the gallery's thumbnail crop never reaches this surface. */
export default function PhotoViewer({ photos, index, onIndexChange, onClose }: {
  photos: ViewerPhoto[]; index: number; onIndexChange: (index: number) => void; onClose: () => void;
}) {
  const photo = photos[index];
  const [imageFailed, setImageFailed] = React.useState(false);
  const [retry, setRetry] = React.useState(0);
  const principalEpoch = useSessionStore(state => state.principalEpoch);
  const userId = useSessionStore(state => state.session?.user.id);
  const signed = useQuery({
    queryKey: ['photo-viewer-original', userId, principalEpoch, photo?.bucket, photo?.storagePath],
    enabled: Boolean(photo?.storagePath && !photo.url),
    staleTime: 4 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await getSupabaseClient().storage.from(photo.bucket ?? 'photos').createSignedUrl(photo.storagePath!, 600);
      if (error) throw error;
      return data.signedUrl;
    },
  });
  const url = photo?.url || signed.data;
  React.useEffect(() => setImageFailed(false), [photo?.id, url]);
  if (!photo) return null;
  const previous = () => onIndexChange(Math.max(0, index - 1));
  const next = () => onIndexChange(Math.min(photos.length - 1, index + 1));
  return createPortal(<EditorDialog open title={photo.title || 'Photo'} onClose={onClose} className="photo-viewer"
    status={`${index + 1} of ${photos.length}`}
    onKeyDown={event => {
      if (event.key === 'ArrowLeft') { event.preventDefault(); previous(); }
      if (event.key === 'ArrowRight') { event.preventDefault(); next(); }
    }}
    footerActions={<div className="photo-viewer__navigation">
      <button type="button" className="btn btn-outline" disabled={index === 0} onClick={previous}>Prev</button>
      <button type="button" className="btn btn-outline" disabled={index >= photos.length - 1} onClick={next}>Next</button>
    </div>}>
    <div className="photo-viewer__stage" aria-busy={!url && signed.isFetching}>
      {imageFailed || signed.isError ? <div role="alert">{safeErrorMessage(signed.error, 'This photo could not be loaded.')}
        <button type="button" className="btn subtle" onClick={() => { setImageFailed(false); setRetry(value => value + 1); if (!photo.url) void signed.refetch(); }}>Try again</button>
      </div> : url ? <img key={`${url}-${retry}`} src={url} alt={photo.title || 'Photo'} onError={() => setImageFailed(true)} /> : <p role="status">Loading photo…</p>}
    </div>
  </EditorDialog>, document.body);
}
